// src/sim/fighter.ts — Dövüşçü yaratılışı, capabilities(state) tablosu, zamanlayıcılar (§3 adım 2), girdi tamponu (adım 3)
// ve hareket adımı (adım 6: ivme/sürtünme/dönüş/zıplama/çift zıplama/coyote/tampon/hızlı iniş/yerçekimi ×1.4/terminal/hava dodge
// yerçekimi; dx = vel·DT + rmDx + push → collision.ts). three'siz; sıcak yolda tahsis yok.
// Tahsis disiplini (ölçümle): V8 inline edilmeyen çağrıda Smi-olmayan double argüman/dönüş için HeapNumber tahsis eder → sıcak yol
// yardımcıları yalnız nesne/tam sayı alır, double'lar `R` yazmacıyla (arena.ts) ya da alanlarla geçer; küçük formüller elle inline.
// Durum sözleşmesi (WP2): `stateTick` = duruma giriş tick'i, `stateUntil` = ÖZEL (exclusive) bitiş — durum `tick < stateUntil` iken sürer;
// NO_TICK = süresiz. WP2'nin sahibi olduğu durumlar: Idle/Move/Air/Dodge/AirDodge/Roll(hareket)/Crawl(hareket)/Spawning/Respawning/Falling/Eliminated.
import { Btn, Ev, FState } from '../core/types.ts';
import type { Fighter, InputBuffer, InputFrame, SimWorld } from '../core/types.ts';
import { v3 } from '../core/vec3.ts';
import { T } from '../config/tuning.ts';
import {
  BTN_BITS, CLIP_FPS, GROUND_SNAP, MAX_HIT_GROUPS, NO_ID, NO_TICK, RECENT_HITS_RING, STALE_QUEUE, TICK_DT, TICK_HZ,
} from '../config/constants.ts';
import { btnIndex } from '../config/controls.ts';
import { R, isQueryEx } from './arena.ts';
import { DODGE_DT_RATE, DODGE_LINEAR_STEP, RM_T0, RM_T1, ROLL_STEP } from './dodge.ts';
import { resolveHorizontal, BRAKE_NONE, BRAKE_STICK, BRAKE_CLIP, PUSH_X, PUSH_Z } from './collision.ts';

// ───────────────────────────── capabilities(state) ─────────────────────────────
/** Durum yetenek bitleri (ARCHITECTURE §3 sonu: "hangi alt adımlar atlanır" tek tabloda). */
export const Cap = {
  Input: 1,        // çubuk/tuş girdisi bu durumda okunur (zıplama, hareket)
  Move: 2,         // yerde çubukla ivme/dönüş
  Jump: 4,         // A ile zıplama / çift zıplama
  Gravity: 8,      // yerçekimi + zemin çözümü (Held/Carried/Falling/Eliminated'da yok)
  Push: 16,        // kapsül itişmesine katılır
  Friction: 32,    // yerde sürtünme (knockdown/lying kayması knockdown.ts'de → yok)
  Brake: 64,       // kenar freni (çubuk kuralı); Dodge/Roll her zaman kırpar
  Hurtbox: 128,    // vurulabilir (hits.ts ayrıca invuln/spawnProt bakar)
  Liftable: 256,   // G3 ile kaldırılabilir (yatan)
  AirControl: 512, // havada çubuk ivmesi + hızlı iniş
} as const;

const C = Cap;
const GROUND_ACTOR = C.Gravity | C.Push | C.Friction | C.Brake | C.Hurtbox;   // hamle/tutma/taunt: yerde durur, kayar, frenler
const CAPS: Int32Array = new Int32Array(32);
CAPS[FState.Idle] = C.Input | C.Move | C.Jump | GROUND_ACTOR;
CAPS[FState.Move] = C.Input | C.Move | C.Jump | GROUND_ACTOR;
CAPS[FState.Air] = C.Input | C.Jump | C.Gravity | C.Push | C.Hurtbox | C.AirControl;
CAPS[FState.Dodge] = C.Gravity | C.Push | C.Brake | C.Hurtbox;
CAPS[FState.AirDodge] = C.Gravity | C.Push | C.Hurtbox;
CAPS[FState.Block] = C.Input | C.Move | GROUND_ACTOR;          // blokta zıplama yok (DESIGN §2 tablosu)
CAPS[FState.Attack] = GROUND_ACTOR;                           // girdi moves.ts'de; keepMomentum sürtünmeyi kapatır
CAPS[FState.Taunt] = GROUND_ACTOR;
CAPS[FState.GrabAttempt] = GROUND_ACTOR;
CAPS[FState.Hold] = GROUND_ACTOR;
CAPS[FState.Lifting] = GROUND_ACTOR;
CAPS[FState.Held] = C.Hurtbox;                                // konum tutanın soketinden (grab.ts)
CAPS[FState.PickingUp] = GROUND_ACTOR;
CAPS[FState.Carrying] = C.Input | C.Move | GROUND_ACTOR;      // ×0.55 hız, ×0.7 dönüş; zıplama yok
CAPS[FState.Carried] = C.Hurtbox;
CAPS[FState.Throwing] = GROUND_ACTOR;
CAPS[FState.Hitstun] = C.Gravity | C.Friction | C.Hurtbox;    // itişme yok, fren yok (knockback)
CAPS[FState.Launched] = C.Gravity | C.Hurtbox;
CAPS[FState.Tumble] = C.Gravity | C.Hurtbox;                  // tumbleControl → zıplama/hava dodge özel izin
CAPS[FState.Knockdown] = C.Gravity | C.Hurtbox;               // kayma knockdown.ts'de
CAPS[FState.Lying] = C.Gravity | C.Push | C.Hurtbox | C.Liftable;   // üstünden geçilir (Y 0.9)
CAPS[FState.Roll] = C.Gravity | C.Push | C.Brake | C.Hurtbox;   // i-frame invulnUntil ile
CAPS[FState.Crawl] = C.Input | C.Move | C.Gravity | C.Push | C.Brake | C.Hurtbox | C.Liftable;
CAPS[FState.GetUp] = C.Gravity | C.Push | C.Brake | C.Hurtbox;
CAPS[FState.Escaping] = C.Gravity | C.Push | C.Brake | C.Hurtbox;
CAPS[FState.Spawning] = C.Gravity;                            // kapsül yerde, girdi/itişme/hurtbox yok
CAPS[FState.Respawning] = C.Gravity;
CAPS[FState.Falling] = 0;
CAPS[FState.Eliminated] = 0;

/** Durum → yetenek maskesi (Cap bitleri). */
export const capabilities = (state: FState): number => CAPS[state] ?? 0;
export const hasCap = (f: Fighter, cap: number): boolean => (CAPS[f.state]! & cap) !== 0;

// ───────────────────────────── yaratılış ─────────────────────────────
/** Squash türleri (Fighter.squashKind; view FEEL tablosuna eşler). 0 = yok. */
export const SQUASH_NONE = 0, SQUASH_LAND = 1, SQUASH_JUMP = 2, SQUASH_HIT = 3, SQUASH_THROWN = 4;
/** Hava jab hakkı (MoveDef.usesPerAir 2 — A1 tablosu WP3'te; inişte sıfırlanan sayaç için yerel sabit). needsWP0: T.JUMP.airJabUses. */
const AIR_JAB_USES = 2;
const AIR_DODGE_USES = 1;
const DOUBLE_JUMP_USES = 1;

export function createFighter(id: number, charIdx: number, isBot: boolean, weightMult: number, seed: number): Fighter {
  // girdi tamponu world.buffers'ta (createInputBuffer); burada yalnız dövüşçü alanları — hepsi sabit tip (V8 hidden class)
  return {
    id, charIdx, isBot, weightMult, seed,
    pos: v3(), vel: v3(), yaw: 0, prevPos: v3(), prevYaw: 0,
    grounded: true, lastGroundedTick: NO_TICK, onPlatform: -1,
    edgeDist: 0, edgeNx: 1, edgeNz: 0,
    jumpHeld: false, jumpCut: false, doubleJumpLeft: DOUBLE_JUMP_USES, airDodgeLeft: AIR_DODGE_USES, airJabLeft: AIR_JAB_USES,
    fastFall: false, fastFallTick: NO_TICK, airDodgeGravUntil: NO_TICK, airDodgeNoAttackUntil: NO_TICK,
    state: FState.Idle, stateTick: 0, stateUntil: NO_TICK, faceDown: false,
    hitstop: 0, frozenTicks: 0,
    invulnUntil: NO_TICK, armorUntil: NO_TICK, spawnProtUntil: NO_TICK, controlLockUntil: NO_TICK,
    moveIdx: -1, moveTick: 0, phase: 0, stageIdx: 0, stageStartTick: NO_TICK,
    touched: false, whiffed: false, firstHitTick: NO_TICK,
    hitMask: new Int32Array(MAX_HIT_GROUPS),
    rmDx: 0, rmDz: 0,
    comboStep: 0, lastMoveIdx: -1, lastMoveEndTick: NO_TICK,
    lockFamily: -1, lockUntil: NO_TICK,
    rootFrame: -1,
    heavyHeldSince: NO_TICK,
    runContextSince: NO_TICK,
    dodgeStreak: 0, lastDodgeTick: NO_TICK, dodgeDirX: 0, dodgeDirZ: 1, dodgeClip: -1,
    blocking: false, blockSince: NO_TICK, guard: T.BLOCK.guardMax, guardLastHitTick: NO_TICK,
    parryUntil: NO_TICK, parryRefractoryUntil: NO_TICK, parryBonusUntil: NO_TICK, hasShield: false, blockPressTick: NO_TICK,
    damage: 0, stun: 0, stunHoldUntil: NO_TICK,
    lastHitBy: NO_ID, lastHitTick: NO_TICK, recentHitTicks: new Int32Array(RECENT_HITS_RING).fill(NO_TICK), recentHead: 0,
    staleMove: new Int32Array(STALE_QUEUE).fill(-1), staleTick: new Int32Array(STALE_QUEUE).fill(NO_TICK), staleHead: 0,
    comboOnMe: 0, longestComboOnMe: 0, hitstunTicks: 0,
    tumbleAxisX: 1, tumbleAxisZ: 0, tumbleAngle: 0, launchPitch: 0, tumbleControl: false, thrownBy: NO_ID,
    grabTarget: NO_ID, grabbedBy: NO_ID, holdSince: NO_TICK, holdDirX: 0, holdDirZ: 1,
    escapeLeft: 0, escapePresses: 0, carryUntil: NO_TICK, grabStun: 0, windupPresses: 0,
    lyingUntil: NO_TICK, rollUsed: false, getUpKick: false, lyingPlan: T.KNOCKDOWN.lyingTicks, lyingSince: NO_TICK,
    weapon: 'unarmed', weaponItem: NO_ID, weaponItemL: NO_ID, shieldItem: NO_ID,
    lives: T.RESPAWN.lives, respawnAt: NO_TICK, falls: 0, kos: 0, fellAt: NO_TICK,
    squashUntil: NO_TICK, squashKind: SQUASH_NONE, flashUntil: NO_TICK, lastHitKind: -1, lastHitDirX: 0, lastHitDirZ: 1,
  };
}

export function createInputBuffer(): InputBuffer {
  const b = { pressTick: new Int32Array(BTN_BITS), releaseTick: new Int32Array(BTN_BITS) };
  b.pressTick.fill(NO_TICK); b.releaseTick.fill(NO_TICK);
  return b;
}

/** Durum geçişi (tek yer): stateTick = tick, stateUntil özel bitiş (NO_TICK süresiz). */
export function setState(f: Fighter, s: FState, tick: number, until: number = NO_TICK): void {
  f.state = s; f.stateTick = tick; f.stateUntil = until;
  f.frozenTicks = 0;   // donma sayacı durum başına (animMap yaş düzeltmesi; entegrasyon 3. tur)
}

/** Yeniden doğuş / maç başı: dövüşçüyü konuma yerleştirir ve savaş alanlarını sıfırlar (DESIGN §8: dmg 0, stun 0, guard 100, silah yok).
 *  Soğuk yol (double argüman yaw serbest). */
export function placeFighter(f: Fighter, at: { x: number; y: number; z: number }, yaw: number, tick: number): void {
  f.pos.x = at.x; f.pos.y = at.y; f.pos.z = at.z;
  f.prevPos.x = at.x; f.prevPos.y = at.y; f.prevPos.z = at.z;
  f.vel.x = 0; f.vel.y = 0; f.vel.z = 0;
  f.yaw = yaw; f.prevYaw = yaw;
  f.grounded = true; f.lastGroundedTick = tick;
  f.jumpHeld = false; f.jumpCut = false; f.doubleJumpLeft = DOUBLE_JUMP_USES; f.airDodgeLeft = AIR_DODGE_USES; f.airJabLeft = AIR_JAB_USES;
  f.fastFall = false; f.fastFallTick = NO_TICK; f.airDodgeGravUntil = NO_TICK; f.airDodgeNoAttackUntil = NO_TICK;
  f.hitstop = 0; f.frozenTicks = 0; f.invulnUntil = NO_TICK; f.armorUntil = NO_TICK;
  f.moveIdx = -1; f.moveTick = 0; f.phase = 0; f.stageIdx = 0; f.stageStartTick = NO_TICK;
  f.touched = false; f.whiffed = false; f.firstHitTick = NO_TICK; f.hitMask.fill(0); f.rmDx = 0; f.rmDz = 0;
  f.comboStep = 0; f.lastMoveIdx = -1; f.lastMoveEndTick = NO_TICK; f.lockFamily = -1; f.lockUntil = NO_TICK; f.rootFrame = -1;
  f.heavyHeldSince = NO_TICK; f.runContextSince = NO_TICK;
  f.dodgeStreak = 0; f.lastDodgeTick = NO_TICK; f.dodgeClip = -1;
  f.blocking = false; f.blockSince = NO_TICK; f.guard = T.BLOCK.guardMax; f.guardLastHitTick = NO_TICK;
  f.parryUntil = NO_TICK; f.parryRefractoryUntil = NO_TICK; f.parryBonusUntil = NO_TICK; f.hasShield = false;
  f.damage = 0; f.stun = 0; f.stunHoldUntil = NO_TICK;
  f.lastHitBy = NO_ID; f.lastHitTick = NO_TICK; f.recentHitTicks.fill(NO_TICK); f.recentHead = 0;
  f.staleMove.fill(-1); f.staleTick.fill(NO_TICK); f.staleHead = 0;
  f.comboOnMe = 0; f.hitstunTicks = 0;
  f.tumbleAngle = 0; f.launchPitch = 0; f.tumbleControl = false;
  f.grabTarget = NO_ID; f.grabbedBy = NO_ID; f.holdSince = NO_TICK; f.escapeLeft = 0; f.escapePresses = 0; f.carryUntil = NO_TICK; f.grabStun = 0;
  f.lyingUntil = NO_TICK; f.rollUsed = false; f.getUpKick = false; f.faceDown = false;
  f.weapon = 'unarmed'; f.weaponItem = NO_ID; f.weaponItemL = NO_ID; f.shieldItem = NO_ID;
  f.squashUntil = NO_TICK; f.squashKind = SQUASH_NONE; f.flashUntil = NO_TICK;
}

// ───────────────────────────── adım 2: zamanlayıcılar ─────────────────────────────
const STUN_DECAY_T = T.COMBAT.stunDecay * TICK_DT;
const TAUNT_DECAY_T = T.COMBAT.tauntDecay * TICK_DT;
const GUARD_REGEN_T = T.BLOCK.regenPerSec * TICK_DT;

/** Dövüşçü zamanlayıcıları. Döner: bu tick hitstop'ta mı (true → adım 4–6 atlanır; yalnız sayaç düştü, tampon yaşlanmadı). */
export function stepTimers(world: SimWorld, f: Fighter): boolean {
  const tick = world.tick;
  if (f.hitstop > 0) {
    f.hitstop--;
    f.frozenTicks++;   // görsel bu tick donuyor (animMap yaştan düşer); durum değişince setState sıfırlar
    // DESIGN §11: kendi hitstop'unda tampon YAŞLANMAZ → tüm kayıtlı basış/bırakış tick'leri 1 ileri kayar
    const buf = world.buffers[f.id]!;
    for (let i = 0; i < BTN_BITS; i++) {
      if (buf.pressTick[i]! !== NO_TICK) buf.pressTick[i]!++;
      if (buf.releaseTick[i]! !== NO_TICK) buf.releaseTick[i]!++;
    }
    return true;
  }
  // Stun sönmesi (DESIGN §5): son isabetten 36 t sonra −30/s; Taunt −40/s ek, gecikmesiz; yer ailesinde (Knockdown/Lying/Roll/Crawl) sönmez
  if (f.stun > 0) {
    const s = f.state;
    const onGround = s === FState.Knockdown || s === FState.Lying || s === FState.Roll || s === FState.Crawl;
    if (!onGround) {
      let dec = 0;
      if (f.lastHitTick === NO_TICK || tick - f.lastHitTick >= T.COMBAT.stunDecayDelay) dec += STUN_DECAY_T;
      if (s === FState.Taunt) dec += TAUNT_DECAY_T;
      const ns = f.stun - dec;
      f.stun = ns < 0 ? 0 : ns;
    }
  }
  // Guard dolumu (DESIGN §3): son bloklu isabetten 1 s sonra 25/s
  const gmax = f.hasShield ? T.BLOCK.shieldGuardMax : T.BLOCK.guardMax;
  if (f.guard < gmax && (f.guardLastHitTick === NO_TICK || tick - f.guardLastHitTick >= T.BLOCK.regenDelayTicks)) {
    const g = f.guard + GUARD_REGEN_T;
    f.guard = g > gmax ? gmax : g;
  }
  // Doğuş koruması bitişi olayı (i-frame/armor/kilitler "until" karşılaştırmasıyla biter; olay yalnız korumada)
  if (f.spawnProtUntil === tick) world.events.push(Ev.SpawnProtEnd, tick, f.id);
  // WP2 durum süreleri (özel bitiş)
  if (f.stateUntil !== NO_TICK && tick >= f.stateUntil) {
    switch (f.state) {
      case FState.Dodge: setState(f, FState.Idle, tick); break;
      case FState.AirDodge: setState(f, f.grounded ? FState.Idle : FState.Air, tick); break;
      case FState.Roll: setState(f, FState.Lying, tick, f.lyingUntil); break;   // knockdown.ts kalan Lying süresini yönetir
      case FState.Spawning: case FState.Respawning: setState(f, FState.Idle, tick); break;
      default: break;   // diğer durumların bitişi sahibi paketlerde (moves/grab/knockdown)
    }
  }
  return false;
}

// ───────────────────────────── adım 3: girdi tamponu ─────────────────────────────
const ATTACK_BITS = Btn.Light | Btn.Heavy | Btn.Grab;

export function stepInputBuffer(world: SimWorld, f: Fighter, input: InputFrame): void {
  const tick = world.tick;
  const buf = world.buffers[f.id]!;
  if (input.pressed !== 0) for (let i = 0; i < BTN_BITS; i++) if (input.pressed & (1 << i)) buf.pressTick[i] = tick;
  if (input.released !== 0) for (let i = 0; i < BTN_BITS; i++) if (input.released & (1 << i)) buf.releaseTick[i] = tick;
  // Y basılı süresi (ıska takibi) — tek yer
  if (input.buttons & Btn.Heavy) { if (f.heavyHeldSince === NO_TICK) f.heavyHeldSince = tick; }
  else f.heavyHeldSince = NO_TICK;
  // Koşma bağlamı sayacı (DESIGN §2): yer hızı ≥ 5.5 ∧ çubuk ≥ 0.85; inRunContext() ≥ 10 t sorar
  const mag2 = input.moveX * input.moveX + input.moveZ * input.moveZ;
  const sp2 = f.vel.x * f.vel.x + f.vel.z * f.vel.z;
  if (f.grounded && sp2 >= T.MOVE.runThreshold * T.MOVE.runThreshold && mag2 >= T.MOVE.runStickMin * T.MOVE.runStickMin) {
    if (f.runContextSince === NO_TICK) f.runContextSince = tick;
  } else f.runContextSince = NO_TICK;
  // Doğuş koruması: kendi saldırı/tutma girdisi korumayı hemen bitirir (DESIGN §8)
  if (f.spawnProtUntil > tick && (input.pressed & ATTACK_BITS) !== 0 && (CAPS[f.state]! & Cap.Input) !== 0) {
    f.spawnProtUntil = tick;
    world.events.push(Ev.SpawnProtEnd, tick, f.id);
  }
}

/** Koşma bağlamı: hız ≥ 5.5 ∧ çubuk ≥ 0.85 en az 10 t (DESIGN §2). */
export const inRunContext = (f: Fighter, tick: number): boolean =>
  f.runContextSince !== NO_TICK && tick - f.runContextSince >= T.MOVE.runContextTicks;

/** Tamponda taze basış var mı: tick − pressTick ≤ window. */
export function bufferedPress(world: SimWorld, f: Fighter, btn: Btn, window: number): boolean {
  const t = world.buffers[f.id]!.pressTick[btnIndex(btn)]!;
  return t !== NO_TICK && world.tick - t <= window;
}
/** Basışı tüket (tekrar ateşlemesin). */
export function consumePress(world: SimWorld, f: Fighter, btn: Btn): void {
  world.buffers[f.id]!.pressTick[btnIndex(btn)] = NO_TICK;
}

// ───────────────────────────── adım 6: hareket ─────────────────────────────
/** Dönüş: 14 rad/s; hedefle fark ≥ 150° ise ilk tick'te büyük adım (snap) — kalan 4 t'de normal hızla biter (DESIGN §3 "5 t'de snap").
 *  Durumsuz türetim: SNAP_FIRST = 1 − turnRate·DT·(snapTurnTicks − 1) / snapRad (WP2 kararı; alan yok, prevYaw'a bağlanmadı). */
const SNAP_RAD = T.MOVE.snapTurnDeg * (Math.PI / 180);
const SNAP_FIRST_FRAC = 1 - (T.MOVE.turnRate * TICK_DT * (T.MOVE.snapTurnTicks - 1)) / SNAP_RAD;
const TAU = Math.PI * 2;
const TURN_STEP = T.MOVE.turnRate * TICK_DT;
const CARRY_TURN_STEP = TURN_STEP * T.MOVE.carryTurnMult;
const GROUND_ACCEL_T = T.MOVE.groundAccel * TICK_DT;
const GROUND_FRICTION_T = T.MOVE.groundFriction * TICK_DT;
const AIR_ACCEL_T = T.MOVE.airAccel * TICK_DT;
const WALK_DEAD = T.MOVE.walkDead, WALK_FULL = T.MOVE.walkFull;
const SQUASH_JUMP_TICKS = Math.round(T.FEEL.squashJumpMs / 1000 * TICK_HZ);
const SQUASH_LAND_TICKS = Math.round(T.FEEL.squashLandMs / 1000 * TICK_HZ);

/** Sıcak yol dönüşü: hedef yaw R[12], adım (rad/tick) R[13]. */
function turnR(f: Fighter): void {
  const target = R[12]!;
  let d = (target - f.yaw) % TAU;
  if (d > Math.PI) d -= TAU; else if (d <= -Math.PI) d += TAU;
  const ad = d < 0 ? -d : d;
  let step = R[13]!;
  if (ad >= SNAP_RAD) { const s = ad * SNAP_FIRST_FRAC; if (s > step) step = s; }
  if (ad <= step) { f.yaw = target; return; }
  let y = (f.yaw + (d > 0 ? step : -step)) % TAU;
  if (y > Math.PI) y -= TAU; else if (y <= -Math.PI) y += TAU;
  f.yaw = y;
}
/** Dışa açık dönüş (WP3 r fazı vb.; soğuk yol): rate rad/s. */
export function turnToward(f: Fighter, targetYaw: number, rate: number): void { R[12] = targetYaw; R[13] = rate * TICK_DT; turnR(f); }

/** Zıplama (A): vy = v0; coyote sonrası da buradan. WP3 'jump' iptali bunu çağırabilir. */
export function doJump(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  f.vel.y = T.JUMP.v0; f.grounded = false; f.lastGroundedTick = NO_TICK;
  f.jumpHeld = true; f.jumpCut = false; f.fastFall = false; f.fastFallTick = NO_TICK;
  f.doubleJumpLeft = DOUBLE_JUMP_USES; f.airDodgeLeft = AIR_DODGE_USES; f.airJabLeft = AIR_JAB_USES;
  f.squashUntil = tick + SQUASH_JUMP_TICKS; f.squashKind = SQUASH_JUMP;
  setState(f, FState.Air, tick);
  consumePress(world, f, Btn.Jump);
  world.events.push(Ev.Jump, tick, f.id);
}
/** Çift zıplama: vy = 9 (atar, eklemez); 1×/hava; tumble'da kontrol dönünce de. */
export function doDoubleJump(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  f.vel.y = T.JUMP.doubleV; f.doubleJumpLeft--;
  f.jumpHeld = true; f.jumpCut = false; f.fastFall = false; f.fastFallTick = NO_TICK;
  if (f.state === FState.Tumble) { f.tumbleControl = false; setState(f, FState.Air, tick); }
  consumePress(world, f, Btn.Jump);
  world.events.push(Ev.DoubleJump, tick, f.id);
}

/** Yere iniş: sayaçlar yenilenir, Ev.Land, squash; Air/AirDodge → Idle. Diğer durumlar (Launched/Attack landPhase) sahibi pakette `grounded` ile çözülür. */
function land(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  f.grounded = true; f.lastGroundedTick = tick; f.vel.y = 0;
  f.jumpHeld = false; f.jumpCut = false; f.fastFall = false; f.fastFallTick = NO_TICK;
  f.doubleJumpLeft = DOUBLE_JUMP_USES; f.airDodgeLeft = AIR_DODGE_USES; f.airJabLeft = AIR_JAB_USES;
  f.airDodgeGravUntil = NO_TICK;
  if (f.state === FState.Air || f.state === FState.AirDodge) setState(f, FState.Idle, tick);
  f.squashUntil = tick + SQUASH_LAND_TICKS; f.squashKind = SQUASH_LAND;
  world.events.push(Ev.Land, tick, f.id);
  // zıplama tamponu (7 t): inişten ≤ 7 t önce basılan A iniş tick'inde ateşler (tampon inişte sıfırlanmaz)
  if (f.state === FState.Idle && bufferedPress(world, f, Btn.Jump, T.JUMP.bufferTicks)) doJump(world, f);
}

/**
 * Hareket adımı (ARCHITECTURE §3 adım 6). Sıra: girdi (zıplama/çift/kesme/hızlı iniş) → yatay kontrol/sürtünme → yerçekimi (dikey
 * integrasyon, yamuk kuralı: v0 = 11 → tepe 2.02) → yatay yer değiştirme (vel·DT + rmDx + push; duvar; kenar freni) → zemin çözümü
 * (snap / kenardan düşme) → edgeDist. İtişme PUSH_X/PUSH_Z[f.id] (collision.computePush). Hitstop'ta çağrılmaz (world).
 * Held/Carried/Falling/Eliminated: hiçbir şey yapmaz.
 */
export function stepLocomotion(world: SimWorld, f: Fighter, input: InputFrame): void {
  const tick = world.tick;
  const caps = CAPS[f.state]!;
  if ((caps & (Cap.Gravity | Cap.Move | Cap.AirControl)) === 0) { f.rmDx = 0; f.rmDz = 0; return; }
  const q = world.query;
  const qx = isQueryEx(q) ? q : null;
  const mx = input.moveX, mz = input.moveZ;
  const magRaw = Math.sqrt(mx * mx + mz * mz);
  const mag = magRaw > 1 ? 1 : magRaw;
  const tumbleCtl = f.state === FState.Tumble && f.tumbleControl;

  // ── zıplama girdileri (tampon 7 t, coyote 6 t / kenar 7 t, çift zıplama) ──
  if ((caps & Cap.Jump) !== 0 || tumbleCtl) {
    if (bufferedPress(world, f, Btn.Jump, T.JUMP.bufferTicks)) {
      let coyote = false;
      if (!f.grounded && f.lastGroundedTick !== NO_TICK) {
        let inside: boolean;
        if (qx) { R[0] = f.pos.x; R[1] = f.pos.z; inside = qx.isInsideR(); } else inside = q.isInside(f.pos.x, f.pos.z);
        coyote = tick - f.lastGroundedTick <= (inside ? T.JUMP.coyoteTicks : T.JUMP.edgeCoyoteTicks);
      }
      if (!tumbleCtl && (f.grounded || coyote)) doJump(world, f);
      else if (!f.grounded && f.doubleJumpLeft > 0) doDoubleJump(world, f);
    }
    // erken bırakma: vy ×0.45 (bir kez, yükselirken)
    if (f.jumpHeld && (input.buttons & Btn.Jump) === 0) {
      f.jumpHeld = false;
      if (f.vel.y > 0 && !f.jumpCut) { f.vel.y *= T.JUMP.cutMult; f.jumpCut = true; }
    }
  }
  // hızlı iniş: L1 yalnız BASMA KENARINDA ∧ vy ≤ 0 (apex sonrası); basılı tutma yerde bloğa dönüşür (block.ts)
  if ((caps & Cap.AirControl) !== 0 && !f.grounded && (input.pressed & Btn.Block) !== 0 && f.vel.y <= 0 && !f.fastFall) {
    if (f.vel.y > T.JUMP.fastFallVy) f.vel.y = T.JUMP.fastFallVy;
    f.fastFall = true; f.fastFallTick = tick;   // rampa başlangıcı (animMap kök pitch; entegrasyon 3. tur)
    world.events.push(Ev.FastFall, tick, f.id);
  }

  // ── yatay kontrol ──
  const dirX = magRaw > 0 ? mx / magRaw : 0, dirZ = magRaw > 0 ? mz / magRaw : 0;
  if (f.grounded) {
    if ((caps & Cap.Move) !== 0) {
      if (f.state === FState.Crawl) {
        // sürünme: 1.5 u/s sabit, yön çubuk (ivme yok)
        if (mag > WALK_DEAD) {
          f.vel.x = dirX * T.MOVE.crawlSpeed; f.vel.z = dirZ * T.MOVE.crawlSpeed;
          R[12] = Math.atan2(dirX, dirZ); R[13] = TURN_STEP; turnR(f);
        } else { f.vel.x = 0; f.vel.z = 0; }
      } else if (mag > WALK_DEAD) {
        // hız çarpanı: blok ×0.20, taşıma ×0.55, 2H ×0.85 (asa belgede açık değil → yalnız '2h'; WP2 kararı)
        let mult = f.state === FState.Block ? T.MOVE.blockSpeedMult : f.state === FState.Carrying ? T.MOVE.carrySpeedMult : 1;
        if (f.weapon === '2h') mult *= T.MOVE.twoHandSpeedMult;
        let tt = (mag - WALK_DEAD) / (WALK_FULL - WALK_DEAD); if (tt < 0) tt = 0; else if (tt > 1) tt = 1;
        const target = tt * tt * (3 - 2 * tt) * T.MOVE.runSpeed * mult;
        const tx = dirX * target, tz = dirZ * target;
        const ex = tx - f.vel.x, ez = tz - f.vel.z;
        const el = Math.sqrt(ex * ex + ez * ez);
        if (el <= GROUND_ACCEL_T) { f.vel.x = tx; f.vel.z = tz; }
        else { f.vel.x += ex / el * GROUND_ACCEL_T; f.vel.z += ez / el * GROUND_ACCEL_T; }
        if (f.state !== FState.Block) { R[12] = Math.atan2(dirX, dirZ); R[13] = f.state === FState.Carrying ? CARRY_TURN_STEP : TURN_STEP; turnR(f); }
        if (f.state === FState.Idle) setState(f, FState.Move, tick);
      } else {
        // sürtünme 55 u/s² (yerinde; ayrı fonksiyon Maglev'de kalıp double'ları kutuluyordu)
        const sp = Math.sqrt(f.vel.x * f.vel.x + f.vel.z * f.vel.z);
        if (sp <= GROUND_FRICTION_T) { f.vel.x = 0; f.vel.z = 0; }
        else { const kf = (sp - GROUND_FRICTION_T) / sp; f.vel.x *= kf; f.vel.z *= kf; }
        if (f.state === FState.Move && f.vel.x === 0 && f.vel.z === 0) setState(f, FState.Idle, tick);
      }
    } else if ((caps & Cap.Friction) !== 0) {
      // hamle: keepMomentum (Dash/Leap) koşu hızını korur, aksi hâlde sürtünme
      const keep = f.state === FState.Attack && f.moveIdx >= 0 && world.moves[f.moveIdx]?.keepMomentum === true;
      if (!keep) {
        const sp = Math.sqrt(f.vel.x * f.vel.x + f.vel.z * f.vel.z);
        if (sp <= GROUND_FRICTION_T) { f.vel.x = 0; f.vel.z = 0; }
        else { const kf = (sp - GROUND_FRICTION_T) / sp; f.vel.x *= kf; f.vel.z *= kf; }
      }
    }
  } else if ((caps & Cap.AirControl) !== 0 || tumbleCtl) {
    if (mag > WALK_DEAD) {
      const prev = Math.sqrt(f.vel.x * f.vel.x + f.vel.z * f.vel.z);
      f.vel.x += dirX * AIR_ACCEL_T * mag;
      f.vel.z += dirZ * AIR_ACCEL_T * mag;
      const sp = Math.sqrt(f.vel.x * f.vel.x + f.vel.z * f.vel.z);
      let cap = prev < sp ? prev : sp; if (cap < T.MOVE.maxAirSpeed) cap = T.MOVE.maxAirSpeed;   // kontrol hızı 7'yi aşmaz; knockback fazlası korunur
      if (sp > cap) { const k = cap / sp; f.vel.x *= k; f.vel.z *= k; }
      if (f.state === FState.Air) { R[12] = Math.atan2(dirX, dirZ); R[13] = TURN_STEP; turnR(f); }
    }
  }

  // ── dodge / roll kök hareketi → rmDx/rmDz (dodge.ts dodgeRootMotion ile aynı; yerinde: ayrı fonksiyon Maglev'de kalıp kutuluyordu) ──
  if (f.state === FState.Roll) {
    const k = tick - f.stateTick;   // 0..20: her tick 1.3/21 u
    if (k >= 0 && k < T.KNOCKDOWN.rollTicks) { f.rmDx += f.dodgeDirX * ROLL_STEP; f.rmDz += f.dodgeDirZ * ROLL_STEP; }
  } else if (f.state === FState.Dodge || f.state === FState.AirDodge) {
    const air = f.state === FState.AirDodge;
    const k = tick - f.stateTick;   // 0..N−1: durum tick'i
    const from = air ? T.DODGE.air.iframeFrom : T.DODGE.iframeFrom;
    if (k === from) {
      // i-frame penceresi: [from, from + len); len = max(3, (to − from + 1) − 3·streak) — DESIGN §3 "2–12 t" dâhil = 11 t (§7 "kısa (11 t)")
      const to = air ? T.DODGE.air.iframeTo : T.DODGE.iframeTo;
      let len = (to - from + 1) - f.dodgeStreak * T.DODGE.streakPenalty;
      if (len < T.DODGE.streakMinIframes) len = T.DODGE.streakMinIframes;
      f.invulnUntil = f.stateTick + from + len;
    }
    // havada darbe fizikten; kök hareket bastır (ANIMATION §2.1 AirDodge). Yerde: ÖLÇEKLE — delta klip [k, k+1] karesinden
    // (ARCHITECTURE §4.6; zaman tabanı notu dodge.ts dodgeRootMotion'da), s(f) = lungeFwd·ax + lungeLeft·az, ds·distance/(s(t1)−s(t0)).
    if (!air && k >= 0 && k < T.DODGE.ticks) {
      if (f.dodgeClip < 0) { f.rmDx += f.dodgeDirX * DODGE_LINEAR_STEP; f.rmDz += f.dodgeDirZ * DODGE_LINEAR_STEP; }
      else {
        const tPrev = k * DODGE_DT_RATE, tNow = (k + 1) * DODGE_DT_RATE;   // clipStart 0
        if (tNow > RM_T0 && tPrev < RM_T1) {
          const cd = world.clips.byIndex(f.dodgeClip);
          const last = cd.frames - 1;
          let fa = Math.round((tPrev < RM_T0 ? RM_T0 : tPrev > RM_T1 ? RM_T1 : tPrev) * CLIP_FPS); if (fa > last) fa = last;
          let fb = Math.round((tNow < RM_T0 ? RM_T0 : tNow > RM_T1 ? RM_T1 : tNow) * CLIP_FPS); if (fb > last) fb = last;
          if (fa !== fb) {
            let fs = Math.round(RM_T0 * CLIP_FPS); if (fs > last) fs = last;
            let fe = Math.round(RM_T1 * CLIP_FPS); if (fe > last) fe = last;
            const ax = cd.lungeAxis[0], az = cd.lungeAxis[1];
            const lf = cd.lungeFwd, ll = cd.lungeLeft;
            const den = (lf[fe]! * ax + ll[fe]! * az) - (lf[fs]! * ax + ll[fs]! * az);
            if (den >= 1e-6 || den <= -1e-6) {   // build-clipdata > 0.05 u doğrular; savunma
              const ds = ((lf[fb]! * ax + ll[fb]! * az) - (lf[fa]! * ax + ll[fa]! * az)) * T.DODGE.distance / den;
              f.rmDx += f.dodgeDirX * ds; f.rmDz += f.dodgeDirZ * ds;
            }
          }
        }
      }
    }
  }

  // ── yerçekimi (dikey integrasyon) ──
  const yBefore = f.pos.y;
  if ((caps & Cap.Gravity) !== 0 && !f.grounded) {
    let g = T.JUMP.gravity;
    if (f.vel.y <= 0) g *= T.JUMP.fallGravMult;                                   // düşerken ×1.4
    if (f.state === FState.AirDodge && tick < f.airDodgeGravUntil) g *= T.DODGE.air.gravMult;   // hava dodge ×0.3 (18 t)
    let vNew = f.vel.y + g * TICK_DT; if (vNew < T.JUMP.terminal) vNew = T.JUMP.terminal;
    f.pos.y += 0.5 * (f.vel.y + vNew) * TICK_DT;
    f.vel.y = vNew;
  }

  // ── yatay yer değiştirme: dx = vel·DT + rmDx + push; duvar; kenar freni (R[8..11] → collision.resolveHorizontal) ──
  R[8] = f.vel.x * TICK_DT + f.rmDx + PUSH_X[f.id]!;
  R[9] = f.vel.z * TICK_DT + f.rmDz + PUSH_Z[f.id]!;
  R[10] = mx; R[11] = mz;
  f.rmDx = 0; f.rmDz = 0;
  const brake = (f.state === FState.Dodge || f.state === FState.Roll) ? BRAKE_CLIP : (caps & Cap.Brake) !== 0 ? BRAKE_STICK : BRAKE_NONE;
  resolveHorizontal(world, f, brake);

  // ── zemin çözümü ──
  if ((caps & Cap.Gravity) !== 0) {
    let g: number;
    if (qx) { R[0] = f.pos.x; R[1] = f.pos.z; R[2] = yBefore > f.pos.y ? yBefore : f.pos.y; qx.groundYR(); g = R[3]!; }
    else g = q.groundY(f.pos.x, f.pos.z, yBefore > f.pos.y ? yBefore : f.pos.y);
    if (g === g && f.vel.y <= 0 && f.pos.y <= g + GROUND_SNAP && yBefore >= g - GROUND_SNAP) {
      if (!f.grounded) land(world, f);   // tamponlu zıplama inişte tekrar havalandırabilir (grounded false kalır)
      f.pos.y = g;
      if (f.grounded) {
        f.vel.y = 0; f.lastGroundedTick = tick;
        if (qx) { R[0] = f.pos.x; R[1] = f.pos.z; f.onPlatform = qx.platformAtR(); } else f.onPlatform = 0;
      }
    } else if (f.grounded) {
      // kenardan düşme (zıplamasız): coyote için lastGroundedTick korunur
      f.grounded = false; f.lastGroundedTick = tick; f.onPlatform = -1;
      if (f.state === FState.Idle || f.state === FState.Move) setState(f, FState.Air, tick);
    }
    // kenar mesafesi (bot/HUD); Tumble'da dışarı çıkış → Ev.EdgeAlarm
    const prevEdge = f.edgeDist;
    if (qx) { R[0] = f.pos.x; R[1] = f.pos.z; qx.edgeDistR(); f.edgeDist = R[3]!; f.edgeNx = R[4]!; f.edgeNz = R[5]!; }
    else { f.edgeDist = q.edgeDist(f.pos.x, f.pos.z, NV); f.edgeNx = NV.x; f.edgeNz = NV.z; }
    if (f.state === FState.Tumble && f.edgeDist < 0 && prevEdge >= 0) world.events.push(Ev.EdgeAlarm, tick, f.id);
  }
}
const NV = v3();

/** Bakış yönü (dünya) — dodge/hamle yönü için kısayol (soğuk yol). */
export const facingX = (f: Fighter): number => Math.sin(f.yaw);
export const facingZ = (f: Fighter): number => Math.cos(f.yaw);
